import { useRef } from "react";
import { useForm } from "react-hook-form";
import { nanoid } from "nanoid";
import cx from "classnames";
import { useAddRequest } from "hooks/api/requests";
import { Input } from "components/Interactive/Input";
import { Button } from "components/Interactive/Button";
import { Select } from "components/Interactive/Select";
import { ReactPortal } from "components/Common/ReactPortal";
import { RequestsContent } from "content";
import { logSuccess } from "utils/notifications";
import { REQUEST_TYPE_OPTIONS } from "utils/options";
import { normalizeTelegramNickname } from "utils/common";
import { TSelectOption } from "types/app/select";
import { ERequestType, TAddRequestBody } from "types/api/requests";
import { TAddRequestForm } from "./AddRequestForm.types";
import "./AddRequestForm.styles.scss";

type TAddRequestFormProps = {
  /**
   * Override or extend the styles applied to the component.
   */
  className?: string;
  /**
   * The function to call when the form is submitted.
   */
  onSubmit?: () => void;
};

export const AddRequestForm: React.FC<TAddRequestFormProps> = (props) => {
  const { className, onSubmit } = props;

  const idRef = useRef<string>(nanoid());

  const { mutate: addRequest } = useAddRequest();

  const [initialType] = REQUEST_TYPE_OPTIONS;

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TAddRequestForm>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      phone: "",
      telegram: "",
      type: initialType,
    },
  });

  const handleAddTeacherSubmit = (formData: TAddRequestForm) => {
    const requestBody: TAddRequestBody = {
      name: formData.name,
      phone: formData.phone.trim() || null,
      telegram: normalizeTelegramNickname(formData.telegram),
      type: formData.type?.value ?? initialType.value,
    };

    addRequest(requestBody, {
      onSuccess: () => {
        onSubmit?.();
        logSuccess(RequestsContent.Add.Notification.SUCCESS);
        reset();
      },
    });
  };

  return (
    <form
      id={idRef.current}
      className={cx(["nb-sub-add-request-form", className])}
      onSubmit={handleSubmit(handleAddTeacherSubmit)}
    >
      <Input<TAddRequestForm>
        name="name"
        id="add-request-name"
        label={RequestsContent.Add.Input.Name.LABEL}
        placeholder={RequestsContent.Add.Input.Name.PLACEHOLDER}
        register={register}
        errors={errors}
        autoFocus
        required
        fullWidth
      />
      <Input<TAddRequestForm>
        name="phone"
        type="tel"
        id="add-request-phone"
        label={RequestsContent.Add.Input.Phone.LABEL}
        placeholder={RequestsContent.Add.Input.Phone.PLACEHOLDER}
        minLength={12}
        register={register}
        errors={errors}
        fullWidth
      />
      <Input<TAddRequestForm>
        name="telegram"
        id="add-request-telegram"
        label={RequestsContent.Add.Input.Telegram.LABEL}
        placeholder={RequestsContent.Add.Input.Telegram.PLACEHOLDER}
        register={register}
        errors={errors}
        fullWidth
      />
      <Select<TAddRequestForm, TSelectOption<ERequestType>>
        name="type"
        id="add-request-type"
        label={RequestsContent.Add.Input.Type.LABEL}
        placeholder={RequestsContent.Add.Input.Type.PLACEHOLDER}
        options={REQUEST_TYPE_OPTIONS}
        control={control}
        errors={errors}
        required
        fullWidth
      />
      <ReactPortal wrapperId="add-request-modal-footer">
        <Button variant="primary" type="submit" form={idRef.current} fullWidth>
          {RequestsContent.Add.SUBMIT}
        </Button>
      </ReactPortal>
    </form>
  );
};
